import "./styles/brand.css";
import "./styles/main.css";
import { store } from "./state/store";
import { STEPS, stepIndexById } from "./steps";
import { SECTIONS, sectionLabel, Step, StepContext } from "./steps/types";
import { clear, errorBox, h } from "./components/ui";
import { advisorLabel } from "./logic/advisors";
import { base64ToBytes, createProcess, IS_DEMO, makeVorgangsnummer, submitAnalyse, SubmitResult } from "./api/client";

const app = document.getElementById("app")!;
const startedAt = Date.now();

let current = 0; // Index in STEPS; === STEPS.length => Erfolgsseite
let submitted = false;
let submitting = false;
let result: SubmitResult | null = null;

/* ---------------- Toast (Gamification) ---------------- */
const toastEl = h("div", { class: "toast" }, h("span", { class: "toast__icon", text: "✓" }), h("span", { class: "toast__text" }));
document.body.appendChild(toastEl);
let toastTimer: number | undefined;
function toast(msg: string) {
  (toastEl.querySelector(".toast__text") as HTMLElement).textContent = msg;
  toastEl.classList.add("toast--show");
  if (toastTimer) window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => toastEl.classList.remove("toast--show"), 2200);
}

/* ---------------- Context für Schritte ---------------- */
const ctx: StepContext = {
  store,
  rerender: () => render(),
  goNext: () => handleNext(),
  goPrev: () => { if (current > 0) { current--; render(); scrollTop(); } },
  goTo: (id: string) => { const i = stepIndexById(id); if (i >= 0) { current = i; render(); scrollTop(); } },
  toast,
};

/* ---------------- Fortschrittsring ---------------- */
function progressRing(pct: number): HTMLElement {
  const r = 24, c = 2 * Math.PI * r;
  const off = c * (1 - pct / 100);
  const ns = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(ns, "svg");
  svg.setAttribute("viewBox", "0 0 56 56");
  svg.setAttribute("class", "ring");
  const mk = (cls: string, extra: Record<string, string> = {}) => {
    const ci = document.createElementNS(ns, "circle");
    ci.setAttribute("cx", "28"); ci.setAttribute("cy", "28"); ci.setAttribute("r", String(r));
    ci.setAttribute("fill", "none"); ci.setAttribute("stroke-width", "5"); ci.setAttribute("class", cls);
    for (const [k, v] of Object.entries(extra)) ci.setAttribute(k, v);
    return ci;
  };
  svg.appendChild(mk("ring__track"));
  svg.appendChild(mk("ring__bar", {
    "stroke-linecap": "round", "stroke-dasharray": String(c), "stroke-dashoffset": String(off),
    transform: "rotate(-90 28 28)",
  }));
  const t = document.createElementNS(ns, "text");
  t.setAttribute("x", "28"); t.setAttribute("y", "32"); t.setAttribute("text-anchor", "middle");
  t.setAttribute("class", "ring__pct"); t.textContent = `${pct}%`;
  svg.appendChild(t);
  return svg as unknown as HTMLElement;
}

/* ---------------- Shell ---------------- */
function renderShell(step: Step): HTMLElement {
  const chromeSteps = STEPS.filter((s) => s.chrome !== false);
  const posInChrome = chromeSteps.indexOf(step);
  const pct = Math.round(((posInChrome + 1) / chromeSteps.length) * 100);
  const remaining = chromeSteps.length - (posInChrome + 1);
  const minutes = Math.max(1, Math.round(remaining * 0.7));

  // Abschnitts-Dots
  const curSecIdx = SECTIONS.findIndex((s) => s.id === step.section);
  const dots = h("div", { class: "progress__dots" });
  SECTIONS.forEach((_, i) => {
    const cls = i < curSecIdx ? "dot dot--done" : i === curSecIdx ? "dot dot--active" : "dot";
    dots.appendChild(h("div", { class: cls }));
  });

  const progress = h("div", { class: "progress" },
    progressRing(pct),
    h("div", { class: "progress__meta" },
      h("div", { class: "progress__section", text: sectionLabel(step.section) }),
      h("div", { class: "progress__sub", text: remaining > 0 ? `Noch etwa ${minutes} Min · ${pct} %` : "Letzter Schritt" }),
      dots));

  const head = h("div", { class: "step-head" },
    step.eyebrow ? h("div", { class: "step-head__eyebrow", text: step.eyebrow }) : null,
    h("h1", { class: "step-head__title", text: step.title }),
    step.subtitle ? h("p", { class: "step-head__sub", text: step.subtitle }) : null);

  const errSlot = h("div", { id: "err-slot" });
  const body = step.build(ctx);

  const isLast = step.id === "datenschutz";
  const back = h("button", { class: "btn btn--ghost", type: "button", text: "Zurück", on: { click: () => ctx.goPrev() } });
  const next = h("button", {
    class: "btn btn--primary", type: "button",
    text: isLast ? "Mehrwertanalyse absenden" : "Weiter",
    on: { click: () => handleNext() },
  });
  const nav = h("div", { class: "navbar" }, h("div", { class: "navbar__inner" }, back, next));

  const appbar = h("div", { class: "appbar" },
    h("img", { class: "appbar__logo", src: "/assets/logo-icon.png", alt: "Blacksmith Finance" }),
    h("div", { class: "appbar__title", text: "Mehrwertanalyse" }));

  return h("div", {},
    h("div", { class: "slash-deco" }),
    h("div", { class: "shell" }, appbar, progress, head, errSlot, body),
    nav);
}

/* ---------------- Navigation ---------------- */
async function handleNext() {
  const step = STEPS[current];
  const errors = step.validate?.(store.state) ?? [];
  const slot = document.getElementById("err-slot");
  if (slot) clear(slot);
  if (errors.length) {
    const box = errorBox(errors);
    if (box && slot) { slot.appendChild(box); slot.scrollIntoView({ behavior: "smooth", block: "center" }); }
    return;
  }
  if (step.id === "datenschutz") { void runSubmit(); return; }
  if (step.leaveMessage) toast(step.leaveMessage);
  // Vorgangsnummer beim Verlassen der Startseite sichern (Live: serverseitig).
  if (current === 0 && !store.state.meta.vorgangsnummer) {
    let vn = "";
    try { vn = await createProcess(); } catch { vn = makeVorgangsnummer(); }
    store.update((s) => { if (!s.meta.vorgangsnummer) s.meta.vorgangsnummer = vn; }, false);
  }
  current++;
  render();
  scrollTop();
}

async function runSubmit() {
  if (submitting) return;
  // Honeypot + Mindest-Ausfüllzeit (Bot-Schutz, §44)
  const hp = (document.getElementById("hp-field") as HTMLInputElement | null)?.value;
  if (hp) { return; } // still: stiller Abbruch bei Bot
  if (Date.now() - startedAt < 3000) { toast("Bitte nimm dir kurz Zeit für deine Angaben."); return; }

  submitting = true;
  const btn = document.querySelector(".btn--primary") as HTMLButtonElement | null;
  if (btn) { btn.disabled = true; btn.textContent = "Wird gesendet …"; }

  store.update((s) => {
    if (!s.meta.vorgangsnummer) s.meta.vorgangsnummer = makeVorgangsnummer();
    s.meta.consentAt = new Date().toISOString();
  }, false);

  try {
    // Die Analyse-PDF wird im Live-Betrieb serverseitig in /api/submit erzeugt, dort nach
    // Drive gelegt, dem Berater angehängt und als Base64 für den Download zurückgegeben.
    result = await submitAnalyse(store.state);
    submitted = true;
    current = STEPS.length;
    render();
    scrollTop();
  } catch {
    if (btn) { btn.disabled = false; btn.textContent = "Mehrwertanalyse absenden"; }
    const slot = document.getElementById("err-slot");
    if (slot) { clear(slot); const b = errorBox(["Beim Absenden ist etwas schiefgelaufen. Bitte versuche es erneut."]); if (b) slot.appendChild(b); }
  } finally {
    submitting = false;
  }
}

/* ---------------- Erfolgsseite ---------------- */
async function downloadPdf() {
  // Live: die serverseitig erzeugte PDF aus der Antwort verwenden – exakt die, die
  // auch der Berater erhält. Demo: dieselbe Erzeugung clientseitig (kein Backend).
  let bytes: Uint8Array;
  if (result?.pdfBase64) {
    bytes = base64ToBytes(result.pdfBase64);
  } else {
    const { generateAnalysePdf } = await import("./pdf/analyse-pdf"); // lazy: pdf-lib erst beim Download laden
    bytes = await generateAnalysePdf(store.state, { beraterLabel: advisorLabel(store.state.beraterId) });
  }
  const blob = new Blob([bytes as BlobPart], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const name = `Mehrwertanalyse_${store.state.stammdaten.vorname}_${store.state.stammdaten.nachname}_${store.state.meta.vorgangsnummer}.pdf`
    .replace(/\s+/g, "_");
  a.href = url; a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

function renderSuccess(): HTMLElement {
  const a = store.state;
  const kv = (k: string, v: string) => h("div", { class: "kv__row" },
    h("span", { class: "kv__k", text: k }), h("span", { class: "kv__v", text: v }));

  return h("div", { class: "hero" },
    h("img", { class: "hero__logo", src: "/assets/logo-lockup.png", alt: "Blacksmith Finance" }),
    h("div", { class: "slash-deco" }),
    h("div", { class: "success-mark", text: "✓" }),
    h("div", { class: "hero__eyebrow", text: "Übermittelt" }),
    h("h1", { class: "hero__title", text: "Geschafft." }),
    h("p", { class: "hero__lead", text: "Vielen Dank! Deine Mehrwertanalyse wurde aufgenommen und automatisch an deinen Berater übermittelt." }),
    h("div", { class: "card", style: { marginTop: "20px" } },
      h("div", { class: "kv" },
        kv("Vorgangsnummer", a.meta.vorgangsnummer),
        kv("Dein Berater", advisorLabel(a.beraterId)))),
    IS_DEMO ? h("p", { class: "field__optional", text: "Demo-Hinweis: Versand an Berater, Google-Drive-Ablage und E-Mail werden später angebunden. Der PDF-Download funktioniert bereits vollständig." }) : null,
    h("button", { class: "btn btn--gold btn--block", type: "button", text: "Mehrwertanalyse als PDF herunterladen", style: { marginTop: "18px" }, on: { click: () => void downloadPdf() } }),
    h("button", { class: "btn btn--ghost btn--block", type: "button", text: "Neue Analyse starten", style: { marginTop: "10px" },
      on: { click: () => { store.reset(); submitted = false; result = null; current = 0; render(); scrollTop(); } } }));
}

/* ---------------- Render ---------------- */
function render() {
  clear(app);
  if (current >= STEPS.length) { app.appendChild(renderSuccess()); return; }
  const step = STEPS[current];
  if (step.chrome === false) {
    app.appendChild(h("div", {}, h("div", { class: "slash-deco" }), step.build(ctx)));
    return;
  }
  app.appendChild(renderShell(step));
}

function scrollTop() { window.scrollTo({ top: 0, behavior: "smooth" }); }

/* ---------------- Verlassen-Warnung (§41) ---------------- */
window.addEventListener("beforeunload", (e) => {
  if (!submitted && store.hasMeaningfulData()) {
    e.preventDefault();
    e.returnValue = "";
  }
});

// expose for debugging in demo
void result;

/* Dev-Hilfen (nur im Vite-Dev-Modus, nicht im Produktions-Build). */
if (import.meta.env.DEV) {
  (window as any).__mwa = {
    store,
    goIndex: (i: number) => { current = i; render(); scrollTop(); },
    goTo: (id: string) => { const idx = stepIndexById(id); if (idx >= 0) { current = idx; render(); scrollTop(); } },
    fill: () => {
      const a = store.state;
      a.meta.vorgangsnummer ||= makeVorgangsnummer();
      a.ziele.wichtig = ["Transparenz", "Sicherheit"];
      a.ziele.ziele = ["Vermögen aufbauen", "Familie absichern"];
      Object.assign(a.stammdaten, {
        vorname: "Max", nachname: "Mustermann", geburtsdatum: "1990-05-12", geburtsort: "Köln",
        staatsangehoerigkeit: "deutsch", strasse: "Musterstraße", hausnummer: "12", plz: "50667",
        ort: "Köln", email: "max@example.de", mobil: "+49 170 1234567", familienstand: "ledig",
        hatKinder: "ja", anzahlKinder: "2", alterKinder: "4 und 7", berufsstatus: "angestellt",
        berufsbezeichnung: "Ingenieur",
      });
      Object.assign(a.gesundheit, { koerperlich: "nein", psychisch: "nein", raucher: "nein", risikohobby: "nein" });
      a.altersvorsorge.vorhanden = "nein";
      a.arbeitskraft.vorhanden = "nein";
      a.sachversicherungen.auswahl = ["Privathaftpflicht", "Hausrat"];
      a.vermoegen.vorhanden = "nein";
      Object.assign(a.strom, { eigenerVertrag: "ja", anbieter: "Rheinenergie", abschlag: "65" });
      a.gas.eigenerVertrag = "nein";
      Object.assign(a.krankenversicherung, { gesetzlichVersichert: "ja", anbieter: "Techniker Krankenkasse", bonusprogramme: "ja", bonusprogrammeText: "Vorsorge-Bonus", halt: "Guter Service und schnelle Erstattung" });
      Object.assign(a.einkommen, { bruttoMonat: "4200", nettoMonat: "2700", gehaltszahlungen: "13", nebeneinkuenfte: "0", kindergeld: "500", weitereEinnahmen: "0" });
      a.einkommensnachweis.art = "gehaltsabrechnung";
      a.dokumente.push({ id: "demo1", kategorie: "Einkommensnachweis", originalName: "gehalt.pdf", safeName: "gehalt.pdf", mime: "application/pdf", size: 234000, status: "done", progress: 100, driveFileId: "demo-x", uploadedAt: new Date().toISOString() });
      a.dokumente.push({ id: "demo2", kategorie: "Stromabrechnung", originalName: "strom.pdf", safeName: "strom.pdf", mime: "application/pdf", size: 156000, status: "done", progress: 100, driveFileId: "demo-y", uploadedAt: new Date().toISOString() });
      Object.assign(a.ausgaben, { wohnen: "1100", lebenshaltung: "600", mobilitaet: "250", kreditraten: "0", unterhalt: "0", versicherungen: "120", versZielText: "Private Zusatzversicherungen", abosVereine: "40", sonstige: "80", sparbeitraege: "200", sparZielText: "ETF-Sparplan und Tagesgeld" });
      Object.assign(a.zukunft, { mindestens: "150", komfortabel: "300", einmalig: "5000" });
      a.verantwortung = "unterstuetzung"; a.risiko = 6; a.flexibilitaet = 4; a.praeferenz = "kurzfristige_verluste";
      a.beraterId = "nick";
      Object.assign(a.einwilligungen, { datenschutz: true, verarbeitung: true, uebermittlung: true, weiterleitung: true, driveSpeicherung: true, gesundheit: true, angabenGeprueft: true });
      store.persist(); render();
    },
  };
}

render();
